/**
 * ConfirmDialog - Reusable confirmation dialog component
 *
 * Reduces duplication for common dialog patterns
 */

import { Button } from "@chakra-ui/react"
import {
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogPositioner,
  DialogRoot,
  DialogTitle,
  Portal,
} from "@chakra-ui/react"
import React from "react"

export interface ConfirmDialogProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void | Promise<void>
  title: string
  message: string | React.ReactNode
  confirmText?: string
  cancelText?: string
  confirmColorScheme?: string
  isLoading?: boolean
  children?: React.ReactNode
}

export const ConfirmDialog = React.memo(
  ({
    isOpen,
    onClose,
    onConfirm,
    title,
    message,
    confirmText = "Confirm",
    cancelText = "Cancel",
    confirmColorScheme = "red",
    isLoading = false,
    children,
  }: ConfirmDialogProps) => {
    const handleConfirm = async () => {
      await onConfirm()
      if (!isLoading) {
        onClose()
      }
    }

    return (
      <DialogRoot open={isOpen} onOpenChange={(e) => !e.open && onClose()}>
        <Portal>
          <DialogBackdrop />
          <DialogPositioner
            inset="0"
            display="grid"
            placeItems="center"
            padding={{ base: 4, md: 6 }}
          >
            <DialogContent maxW="md">
              <DialogHeader>
                <DialogTitle>{title}</DialogTitle>
                <DialogCloseTrigger />
              </DialogHeader>
              <DialogBody>{children || message}</DialogBody>
              <DialogFooter>
                <Button variant="ghost" onClick={onClose} disabled={isLoading}>
                  {cancelText}
                </Button>
                <Button
                  colorScheme={confirmColorScheme}
                  onClick={handleConfirm}
                  loading={isLoading}
                  ml={3}
                >
                  {confirmText}
                </Button>
              </DialogFooter>
            </DialogContent>
          </DialogPositioner>
        </Portal>
      </DialogRoot>
    )
  },
)

ConfirmDialog.displayName = "ConfirmDialog"

export default ConfirmDialog
